import { Box } from '@mui/material';
import { ReactNode } from 'react';
import appIcon from '../../../assets/images/pontuax-app-icon.png';
import { buttonInk, rgba } from '../lib/appColors';
import { MDI, MdiName } from '../lib/mdiIcons';
import { ArtRecipe } from '../types';
import HomeBody, { demoHome, HomeSectionView } from './homeSections';

/**
 * As telas do aplicativo desenhadas em HTML, com a arte gerada ATRÁS.
 *
 * O asset é fundo: quem olha o PNG cru não vê que o texto do RN cai numa área
 * clara, nem por que o modelo A deixa o miolo vazio. Aqui o chrome do app é
 * desenhado por cima, ao vivo, e dá para mexer nele — cor, logo, label, prato
 * atrás da logo — antes de decidir qualquer coisa com o cliente.
 *
 * ESPAÇO DE MEDIDAS: pontos do iPhone, 390×844. É o mesmo espaço do código do
 * app (`Welcome/index.js`: fontSize 35 no BEM-VINDO, botão com borderRadius 25
 * e padding 20px 5px, ButtonsContainer com 25% da altura) e exatamente metade
 * do artboard do welcome (1560×3376) — a proporção bate, então a arte entra
 * com `cover` sem distorcer. O header (1280×856) é mais largo que a faixa e o
 * app corta ~3,2% de cada lado, que é o que o `cover` faz aqui também.
 *
 * A escala sai de `width`: o conteúdo é autorado em 390pt e reduzido por
 * transform, para o mesmo componente servir de card pequeno e de tela cheia.
 */

export const SCREEN = { w: 390, h: 844 };
/** Faixa do header na home, em pontos (558px do canvas ÷ 2). */
const HEADER_H = 279;

export interface PlateSettings {
  color: string;
  /** 0 = sem prato (logo direto sobre a arte). */
  opacity: number;
}

/**
 * O welcome NÃO tem cor configurável: BEM-VINDO!, nome e slogan são `#fff`
 * cravado no `Welcome/index.js` (color="#fff" nas duas Styled.Text e
 * Theme.colors.white no markdown do slogan). Só o header da home segue o
 * `appHeaderLabelColor`, e lá ele governa nome, slogan, sino, saldo e o texto
 * e a BORDA do botão Extrato (`HeaderBalance/styles.ts`).
 */
const WELCOME_INK = '#FFFFFF';

export interface MockupSettings {
  brand: string;
  /** `appHeaderLabelColor` — vale só no header da home. */
  ink: string;
  signUp: string;
  signIn: string;
  /**
   * `appShowWelcomeLabel`. É UMA prop só no app e é tudo-ou-nada: liga e
   * desliga BEM-VINDO!, a logo, o nome e o slogan juntos (Welcome/index.js —
   * o bloco inteiro está atrás da condição). Separar isso aqui mostraria uma
   * combinação que o aplicativo não sabe desenhar.
   */
  showWelcomeLabel: boolean;
  /** A logo 110pt do header da home. Independente do welcome — é outra tela. */
  showHeaderLogo: boolean;
  /** `appLogoWidth`, em % da largura da tela. */
  logoWidth: number;
  welcomePlate: PlateSettings;
  headerPlate: PlateSettings;
}

/**
 * O estado inicial do mockup: o modal abre JÁ configurado pela receita daquele
 * modelo, e não em cima de um chute.
 *
 * A linha que separa o que a receita manda do que ela não manda é o próprio
 * `cores` dela, que vem partido em dois de propósito:
 *
 * - `cores.chrome` é CONFIGURAÇÃO — o que o app desenha por cima da arte em
 *   tempo de execução, que é exatamente o que este mockup simula. O gerador
 *   escolheu essas cores olhando o fundo que ele mesmo desenhou (texto branco
 *   porque a faixa ficou escura, por exemplo), então é a melhor sugestão que
 *   existe e é o que a tela abre mostrando. É também o que "Aplicar cores"
 *   grava no cadastro, e por isso o campo salvo continua valendo de fallback:
 *   receita → cadastro → branco.
 * - `cores.gravadas` é PIXEL — está rasterizado no PNG e não há como mudar sem
 *   gerar de novo. Fica fora daqui inteiro. Alimentar `brand` com
 *   `gravadas.brand` pintaria o app de grafite na paleta `neutro` e, pior,
 *   "Aplicar cores" gravaria grafite como a cor DA REDE. `brand` vem do
 *   cadastro, e o alerta do modal é que avisa quando o fundo não vai
 *   acompanhar uma troca de cor.
 *
 * `welcomeTextColor` existe na receita mas não entra: nas boas-vindas o app
 * escreve com `#fff` cravado no `Welcome/index.js`, sem prop nenhuma — ver
 * WELCOME_INK. Simular outra cor ali seria mostrar uma tela que o aplicativo
 * não sabe desenhar.
 *
 * De `app` vêm `showWelcomeLabel` e `logoWidth`, que não são escolha de
 * ninguém: são o que aquele asset já traz desenhado.
 */
export const defaultSettings = (record: {
  color?: string | null;
  headerLabelColor?: string | null;
  signUpButtonColor?: string | null;
  signInButtonColor?: string | null;
}, recipe?: ArtRecipe | null): MockupSettings => {
  const chrome = recipe?.cores?.chrome;

  return {
    brand: record.color || '#3399FF',
    ink: chrome?.headerLabelColor || record.headerLabelColor || '#FFFFFF',
    signUp: chrome?.signUpButtonColor || record.signUpButtonColor || '#FFFFFF',
    signIn: chrome?.signInButtonColor || record.signInButtonColor || '#FFFFFF',
    // Modelo que grava o logo no asset exige `appShowWelcomeLabel = false`. Sem
    // a receita isto era `true` sempre, e a tela abria desenhando o BEM-VINDO! e
    // o logo do RN por cima do logo que o PNG já tinha.
    showWelcomeLabel: recipe?.app?.showWelcomeLabel ?? true,
    showHeaderLogo: true,
    logoWidth: recipe?.app?.logoWidth ?? 50,
    welcomePlate: { color: '#FFFFFF', opacity: 0 },
    headerPlate: { color: '#000000', opacity: 0 },
  };
};

const StatusBar = ({ ink }: { ink: string }) => (
  <Box sx={{
    position: 'absolute', top: 0, left: 0, right: 0, height: 54, px: '22px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    color: ink, zIndex: 3,
  }}>
    <Box sx={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>9:41</Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor">
        <rect x="0" y="8.5" width="3" height="3.5" rx="1" /><rect x="5" y="6" width="3" height="6" rx="1" />
        <rect x="10" y="3" width="3" height="9" rx="1" /><rect x="15" y="0" width="3" height="12" rx="1" />
      </svg>
      <svg width="17" height="12" viewBox="0 0 17 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M1 4.2a11 11 0 0 1 15 0" /><path d="M3.6 7a7.4 7.4 0 0 1 9.8 0" /><path d="M6.3 9.7a3.6 3.6 0 0 1 4.4 0" />
      </svg>
      <svg width="27" height="13" viewBox="0 0 27 13" fill="none">
        <rect x="0.6" y="0.6" width="22" height="11.8" rx="3.2" stroke="currentColor" strokeOpacity=".45" strokeWidth="1.1" />
        <rect x="2.2" y="2.2" width="18.8" height="8.6" rx="2.1" fill="currentColor" />
        <path d="M24.4 4.4v4.2a2.6 2.6 0 0 0 0-4.2Z" fill="currentColor" fillOpacity=".5" />
      </svg>
    </Box>
  </Box>
);

/** Fundo: a arte gerada, ou o campo da marca quando ela ainda não existe. */
const ArtLayer = ({ art, brand }: { art: string | null; brand: string }) => (
  art
    ? <Box component="img" src={art} alt="" sx={{
      position: 'absolute', inset: 0, width: '100%', height: '100%',
      objectFit: 'cover', display: 'block',
    }} />
    : <Box sx={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(160deg, ${brand} 0%, ${rgba(brand, 0.75)} 45%, #101317 100%)`,
    }} />
);

/** Prato atrás da logo. `opacity: 0` deixa a logo direto sobre a arte. */
const Plate = ({ plate, radius, padding, children }: {
  plate: PlateSettings; radius: number; padding: number; children: ReactNode;
}) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: plate.opacity > 0 ? rgba(plate.color, plate.opacity) : 'transparent',
    borderRadius: `${radius}px`,
    // O recorte vale mesmo SEM prato: logo em PNG opaco (fundo branco chapado)
    // é a maioria do que chega, e sem isto ela entra como um retângulo duro
    // sobre a arte. Com o prato, é o que impede o canto quadrado da imagem de
    // furar o canto arredondado do fundo.
    overflow: 'hidden',
    padding: plate.opacity > 0 ? `${padding}px` : 0,
    // Sombra só quando há prato: prato sem sombra sobre foto lê como recorte.
    boxShadow: plate.opacity > 0.6 ? '0 4px 14px rgba(0,0,0,.28)' : 'none',
  }}>
    {children}
  </Box>
);

interface HomeProps extends ScreenProps {
  sections?: HomeSectionView[];
  /** Deixa o corpo rolar. A folha que o `MockupDialog` fotografa não rola. */
  scrollable?: boolean;
}

interface ScreenProps {
  art: string | null;
  logo: string | null;
  name: string;
  slogan: string;
  settings: MockupSettings;
}

const Welcome = ({ art, logo, name, slogan, settings }: ScreenProps) => {
  const { brand, showWelcomeLabel, logoWidth, welcomePlate } = settings;

  return (
    <>
      <ArtLayer art={art} brand={brand} />
      <StatusBar ink={WELCOME_INK} />

      <Box component="img" src={appIcon} alt="" sx={{
        position: 'absolute', right: 10, top: 52, width: 40, height: 40,
        borderRadius: '6px', zIndex: 3,
      }} />

      <Box sx={{
        position: 'absolute', top: 100, left: 0, right: 0, px: '20px', zIndex: 2,
        display: 'flex', flexDirection: 'column', alignItems: 'center', color: WELCOME_INK,
      }}>
        {showWelcomeLabel && (
          <>
          <Box sx={{ fontSize: 35, lineHeight: 1.2, textShadow: '0 3px 12px rgba(0,0,0,.5)' }}>
            BEM-VINDO!
          </Box>

          <Box sx={{ width: `${logoWidth}%`, mt: '30px', mb: '40px' }}>
            <Plate plate={welcomePlate} radius={12} padding={10}>
              {logo
                ? <Box component="img" src={logo} alt="" sx={{
                  width: '100%', height: 'auto', maxHeight: 210, objectFit: 'contain', display: 'block',
                  borderRadius: '10px',
                }} />
                : <Box sx={{
                  height: 110, width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', border: '2px dashed rgba(255,255,255,.5)',
                  borderRadius: '10px', fontSize: 11, opacity: .85,
                }}>logo do cliente</Box>}
            </Plate>
          </Box>

          <Box sx={{
            fontSize: 20, fontWeight: 700, textAlign: 'center',
            textShadow: '0 2px 8px rgba(0,0,0,.5)',
          }}>{name}</Box>
          {!!slogan && (
            <Box sx={{
              fontSize: 20, textAlign: 'center', maxWidth: '84%', mt: '4px',
              textShadow: '0 2px 8px rgba(0,0,0,.5)',
            }}>{slogan}</Box>
          )}
          </>
        )}
      </Box>

      {/* 25% finais: os dois botões que o app desenha. É a faixa que a arte não
          pode ocupar com logo nem texto — some atrás do botão. */}
      <Box sx={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: 211, zIndex: 2,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'flex-end', pb: '20px',
      }}>
        <Box sx={{
          width: '70%', backgroundColor: settings.signUp, color: buttonInk(settings.signUp),
          borderRadius: '25px', padding: '20px 5px', fontSize: 16, fontWeight: 500,
          textAlign: 'center', mb: '10px', lineHeight: 1.375,
        }}>CADASTRE-SE AGORA!</Box>
        <Box sx={{
          width: '50%', backgroundColor: settings.signIn, color: buttonInk(settings.signIn),
          borderRadius: '17.5px', padding: '10px 5px', fontSize: 14, fontWeight: 500,
          textAlign: 'center', mb: '10px', lineHeight: 1.57,
        }}>ENTRAR</Box>
      </Box>
    </>
  );
};

/** Um glifo do MDI no tamanho que o app usa. Preenchido, como na fonte. */
const MdiIcon = ({ name, size, color }: { name: MdiName; size: number; color?: string }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color || 'currentColor'}>
    <path d={MDI[name]} />
  </svg>
);

/**
 * O bottom bar do app, aba por aba, na ordem do `MainNavigator`. O buraco no
 * meio é o `FuelButton`, que não é aba: é um botão redondo por cima da barra.
 */
const TABS: ({ label: string; icon: MdiName } | null)[] = [
  { label: 'Início', icon: 'home-variant' },
  { label: 'Resgatar', icon: 'gift-outline' },
  null,
  { label: 'Cupons', icon: 'ticket-percent-outline' },
  { label: 'Perfil', icon: 'account-circle-outline' },
];

const Home = ({ art, logo, name, slogan, settings, sections, scrollable }: HomeProps) => {
  const { brand, ink, showHeaderLogo, headerPlate } = settings;

  return (
    <>
      <Box sx={{ position: 'relative', height: HEADER_H, flex: 'none', color: ink }}>
        <ArtLayer art={art} brand={brand} />
        <StatusBar ink={ink} />

        <Box sx={{ position: 'absolute', top: 59, left: 0, right: 0, px: '15px', pt: '10px', zIndex: 2 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {showHeaderLogo && (
              <Box sx={{ width: 110, height: 110, mr: '10px', flex: 'none' }}>
                <Plate plate={headerPlate} radius={10} padding={6}>
                  {logo
                    ? <Box component="img" src={logo} alt="" sx={{
                      width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                      borderRadius: '8px',
                    }} />
                    : <Box sx={{
                      width: '100%', height: 98, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', textAlign: 'center', fontSize: 9,
                      border: '1px dashed rgba(255,255,255,.45)', borderRadius: '10px',
                    }}>logo do cliente<br />110pt</Box>}
                </Plate>
              </Box>
            )}

            <Box sx={{
              flex: 1, display: 'flex', flexDirection: 'column',
              justifyContent: 'space-between', minHeight: 100, maxHeight: 110,
            }}>
              <Box>
                <Box sx={{ fontSize: 15, fontWeight: 700 }}>{name}</Box>
                <Box sx={{ fontSize: 10 }}>{slogan}</Box>
              </Box>

              <Box sx={{ position: 'absolute', right: 5, top: 0 }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3.5a5.5 5.5 0 0 0-5.5 5.5c0 4.5-2 6-2 6h15s-2-1.5-2-6A5.5 5.5 0 0 0 12 3.5Z" />
                  <path d="M10.3 18.5a2 2 0 0 0 3.4 0" />
                </svg>
              </Box>

              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                width: '100%', maxHeight: 45,
              }}>
                <Box>
                  <Box sx={{ fontSize: 12 }}>Olá,</Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700, mt: '1px' }}>Cliente PontuaX</Box>
                </Box>
                <Box component="img" src={appIcon} alt="" sx={{
                  width: 38, height: 38, borderRadius: '6px', flex: 'none',
                }} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: '1px', backgroundColor: '#C4C4C4', opacity: .8, my: '8px' }} />

          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 50,
          }}>
            <Box>
              <Box sx={{ fontSize: 13 }}>Saldo em Pontos</Box>
              <Box sx={{ fontSize: 14, fontWeight: 700 }}>1.300,00</Box>
            </Box>
            <Box sx={{
              minWidth: 85, minHeight: 35, padding: '8px 12px', border: '2px solid currentColor',
              borderRadius: '20px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 13, fontWeight: 700, flex: 'none',
            }}>Extrato</Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        position: 'relative', mt: '-20px', backgroundColor: '#fff', flex: 1, minHeight: 0,
        overflowY: scrollable ? 'auto' : 'hidden', overflowX: 'hidden',
        pt: '20px', pb: scrollable ? '96px' : 0,
        borderTopLeftRadius: '12px', borderTopRightRadius: '12px', color: '#16161f',
      }}>
        <HomeBody items={sections || demoHome()} brand={brand} />
      </Box>

      <Box sx={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: 84, backgroundColor: '#fff',
        boxShadow: '0 -4px 12px rgba(0,0,0,.06)', display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)', alignItems: 'start', pt: '8px',
      }}>
        {TABS.map((tab, index) => (
          tab === null ? (
            /* `FuelButton`: 64pt, ícone de 32 em branco, sombra na cor da rede. */
            <Box key="fab" sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <Box sx={{
                position: 'absolute', top: -22, width: 64, height: 64, borderRadius: '32px',
                backgroundColor: brand, display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 8px ${rgba(brand, 0.35)}`,
              }}>
                <MdiIcon name="gas-station" size={32} color="#fff" />
              </Box>
            </Box>
          ) : (
            /* Só a primeira aba está ativa — é onde a home é desenhada. */
            <Box key={tab.label} sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
              fontSize: 11, fontWeight: 600, color: index === 0 ? brand : '#53565c',
            }}>
              <MdiIcon name={tab.icon} size={26} />
              {tab.label}
            </Box>
          )
        ))}
      </Box>
    </>
  );
};

interface Props {
  screen: 'welcome' | 'home';
  /** Artboard gerado. Sem ele, o fundo é o campo da marca. */
  art: string | null;
  logo: string | null;
  name: string;
  slogan: string;
  settings: MockupSettings;
  /** Largura desejada em pixels; a tela é reduzida a partir dos 390pt. */
  width?: number;
  /**
   * As seções da home, na ordem. Sem isto, desenha a home de exemplo do estúdio
   * — é o que permite ao editor de Layout mostrar a home real da rede sem que
   * este componente saiba o que é `appHomeItems`.
   */
  sections?: HomeSectionView[];
  /** Deixa o corpo da home rolar, para a lista que não cabe na dobra. */
  scrollable?: boolean;
}

const AppMockup = ({
  screen, art, logo, name, slogan, settings, width = SCREEN.w, sections, scrollable,
}: Props) => {
  const scale = width / SCREEN.w;

  return (
    <Box sx={{
      width, height: SCREEN.h * scale, flex: 'none',
      borderRadius: `${28 * scale}px`, overflow: 'hidden', backgroundColor: '#fff',
      boxShadow: `0 0 0 ${6 * scale}px #23262c, 0 ${12 * scale}px ${28 * scale}px rgba(0,0,0,.35)`,
    }}>
      <Box sx={{
        width: SCREEN.w, height: SCREEN.h, position: 'relative', overflow: 'hidden',
        transform: `scale(${scale})`, transformOrigin: 'top left',
        display: 'flex', flexDirection: 'column',
        fontFamily: '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        backgroundColor: '#0d0f12',
      }}>
        {screen === 'welcome'
          ? <Welcome art={art} logo={logo} name={name} slogan={slogan} settings={settings} />
          : <Home art={art} logo={logo} name={name} slogan={slogan} settings={settings}
            sections={sections} scrollable={scrollable} />}
      </Box>
    </Box>
  );
};

export default AppMockup;
